import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';

export interface DocumentVerificationState {
    verified: boolean;
    verified_at: string | null;
    verified_by_user_id: string | null;
}

interface UseDocumentVerificationArgs {
    /** Tenant id (the impersonated user's id). When `null` the hook is disabled. */
    tenantId: string | null;
    /** Document id to read/toggle verification for. When `null` the hook is disabled. */
    documentId: string | null;
    /** Master enable switch — caller passes `flag && isAdmin && isImpersonating`. */
    enabled: boolean;
}

const verificationQueryKey = (tenantId: string | null, documentId: string | null) =>
    ['document-verification', tenantId, documentId] as const;

/**
 * Read + toggle the verified state of a document for the impersonated tenant.
 *
 * Backend: see `backend/src/api/v1/endpoints/admin_document_verification.py`. The
 * endpoint set is admin-only and proxies through the BFF with the admin's real
 * identity (per `bff-service.proxy.is_admin_path`). Tenant id flows through the
 * URL because admin paths do not propagate the impersonation context in headers.
 */
export function useDocumentVerification({ tenantId, documentId, enabled }: UseDocumentVerificationArgs) {
    const queryClient = useQueryClient();
    const canQuery = enabled && !!tenantId && !!documentId;

    const stateQuery = useQuery<DocumentVerificationState>({
        queryKey: verificationQueryKey(tenantId, documentId),
        queryFn: async () => {
            const response = await api.get<DocumentVerificationState>(
                `/admin/users/${tenantId}/documents/${documentId}/verification`,
            );
            return response.data;
        },
        enabled: canQuery,
        staleTime: 30_000,
    });

    const verifyMutation = useMutation<DocumentVerificationState, Error>({
        mutationFn: async () => {
            const response = await api.post<DocumentVerificationState>(
                `/admin/users/${tenantId}/documents/${documentId}/verify`,
            );
            return response.data;
        },
        onSuccess: (data) => {
            queryClient.setQueryData(verificationQueryKey(tenantId, documentId), data);
        },
    });

    const unverifyMutation = useMutation<DocumentVerificationState, Error>({
        mutationFn: async () => {
            const response = await api.post<DocumentVerificationState>(
                `/admin/users/${tenantId}/documents/${documentId}/unverify`,
            );
            return response.data;
        },
        onSuccess: (data) => {
            queryClient.setQueryData(verificationQueryKey(tenantId, documentId), data);
        },
    });

    return {
        state: stateQuery.data,
        loading: stateQuery.isLoading,
        verify: verifyMutation.mutateAsync,
        unverify: unverifyMutation.mutateAsync,
        toggling: verifyMutation.isPending || unverifyMutation.isPending,
    };
}
